import { useEffect, useState } from "react";
import {
  clearViewportDiagnostics,
  isViewportDebugEnabled,
  readViewportDiagnostics,
  type ViewportDiagnosticSnapshot,
} from "@/lib/viewportDiagnostics";
import { readOAuthRecoveryDecision, type OAuthRecoveryDecision } from "@/lib/oauthReturn";

/**
 * TEMPORARY debug surface for the Google OAuth mobile scaling investigation.
 * Rendered only when the URL contains `?debugViewport=1`.
 */
export default function ViewportDebugPanel() {
  const [enabled] = useState(() => isViewportDebugEnabled());
  const [snapshots, setSnapshots] = useState<ViewportDiagnosticSnapshot[]>([]);
  const [decision, setDecision] = useState<OAuthRecoveryDecision | null>(null);
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const refresh = () => {
      setSnapshots(readViewportDiagnostics());
      setDecision(readOAuthRecoveryDecision());
    };
    refresh();
    window.addEventListener("tl-vp-diagnostic", refresh);
    const id = window.setInterval(refresh, 1000);
    return () => {
      window.removeEventListener("tl-vp-diagnostic", refresh);
      window.clearInterval(id);
    };
  }, [enabled]);

  if (!enabled) return null;

  const payload = JSON.stringify(
    { userAgent: navigator.userAgent, recoveryDecision: decision, snapshots },
    null,
    2,
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payload);
    } catch {
      const area = document.createElement("textarea");
      area.value = payload;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[10000] max-h-[60vh] overflow-auto border-t border-border bg-background/95 p-3 text-[11px] leading-tight text-foreground shadow-lg backdrop-blur">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-semibold">Viewport diagnostics ({snapshots.length})</span>
        <button className="rounded border border-border px-2 py-1" onClick={copy}>
          {copied ? "Copied" : "Copy diagnostics"}
        </button>
        <button className="rounded border border-border px-2 py-1" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Show"}
        </button>
        <button
          className="rounded border border-border px-2 py-1"
          onClick={() => {
            clearViewportDiagnostics();
            setSnapshots([]);
          }}
        >
          Clear
        </button>
      </div>

      {open && (
        <>
          <div className="mb-2 rounded border border-border p-2">
            <div className="font-semibold">OAuth recovery decision</div>
            {decision ? (
              <div>
                eligible={String(decision.eligible)} · reason={decision.reason} · pending=
                {String(decision.pendingMarkerExists)} · session={String(decision.hasSession)} · expired=
                {String(decision.expired)} · alreadyRecovered={String(decision.alreadyRecovered)} · target=
                {decision.target ?? "—"}

              </div>
            ) : (
              <div>no decision recorded</div>
            )}
          </div>

          <table className="w-full border-collapse">
            <thead>
              <tr className="text-left">
                <th className="pr-2">stage</th>
                <th className="pr-2">ms</th>
                <th className="pr-2">inner</th>
                <th className="pr-2">client</th>
                <th className="pr-2">scrollW</th>
                <th className="pr-2">screen</th>
                <th className="pr-2">vv</th>
                <th className="pr-2">dpr</th>
                <th className="pr-2">md768</th>
                <th className="pr-2">nav</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map((s, i) => (
                <tr key={`${s.stage}-${i}`} className="border-t border-border/50">
                  <td className="pr-2">{s.stage}</td>
                  <td className="pr-2">{s.t}</td>
                  <td className="pr-2">{s.innerWidth}×{s.innerHeight}</td>
                  <td className="pr-2">{s.clientWidth}×{s.clientHeight}</td>
                  <td className="pr-2">{s.docScrollWidth}/{s.bodyScrollWidth}</td>
                  <td className="pr-2">{s.screenWidth}×{s.screenHeight}</td>
                  <td className="pr-2">{s.vvWidth ? Math.round(s.vvWidth) : "—"}@{s.vvScale ?? "—"}</td>
                  <td className="pr-2">{s.dpr}</td>
                  <td className="pr-2">{String(s.md768)}</td>
                  <td className="pr-2">{s.navType ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <pre className="mt-2 whitespace-pre-wrap break-all opacity-70">{payload}</pre>
        </>
      )}
    </div>
  );
}
